"use client";
import React, { useRef, useState, useEffect, memo, useCallback } from "react";
import { Application } from "@splinetool/runtime";
import { useClientContext } from "@/shared/components/Context";
import s from "./styles.module.css";

import { scheduleIdleTask } from "@/shared/utils/scheduleIdleTask";

const Spline = React.lazy(() => import("@splinetool/react-spline"));

interface Props {
  mobileSrc: string;
  desktopSrc: string;
  breakpoint?: number;
}

const DELAY_UNTIL_CANVAS_IN_DOM = 120;

export default memo(function Scene({
  mobileSrc,
  desktopSrc,
  breakpoint = 768,
}: Props) {
  const { setMainFigureSplineRef } = useClientContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const splineRef = useRef<Application | null>(null);
  const [isLoaded, setLoaded] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string>("");

  const updateCanvasSize = useCallback(() => {
    if (splineRef.current?.canvas) {
      const canvas = splineRef.current.canvas;
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      canvas.style.objectFit = "contain";
    }
  }, []);

  const getAppropriateSrc = useCallback(() => {
    return window.innerWidth < breakpoint ? mobileSrc : desktopSrc;
  }, [mobileSrc, desktopSrc, breakpoint]);

  const handleResize = useCallback(() => {
    const newSrc = getAppropriateSrc();
    if (newSrc !== currentSrc) {
      setCurrentSrc(newSrc);
      setLoaded(false);
    }
    updateCanvasSize();
  }, [currentSrc, getAppropriateSrc, updateCanvasSize]);

  useEffect(() => {
    if (isLoaded) {
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isLoaded, handleResize]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          scheduleIdleTask(() => {
            setCurrentSrc(getAppropriateSrc());
            setShouldLoad(true);
          });
        }
      },
      { threshold: 0.1, rootMargin: "200px" }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [getAppropriateSrc]);

  return (
    <div style={{ position: "relative" }} ref={containerRef}>
      <div className={`${s.wrapper} ${isLoaded ? s.ready : s.loading}`}>
        {shouldLoad && currentSrc && (
          <React.Suspense>
            <Spline
              key={currentSrc}
              scene={currentSrc}
              onLoad={(spline: Application) => {
                splineRef.current = spline;
                setMainFigureSplineRef(spline);

                setTimeout(() => {
                  updateCanvasSize();
                  setLoaded(true);
                }, DELAY_UNTIL_CANVAS_IN_DOM);
              }}
            />
          </React.Suspense>
        )}
      </div>
    </div>
  );
});
